import { Flex } from '@driftt/dds'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { getPlaybooks } from 'api/playbooks'
import {
  orgHasPlaybookPriorityFeature,
  orgHasWorkspaceAccess,
  userHasPlaybookPriorityAccess,
} from 'utils/access'

import { PrioritizeModal, Table, TableHeader } from './components'
import PlaybookPriorityAccessWarning from './components/PlaybookPriorityAccessWarning'
import {
  PLAYBOOK_CATEGORIES,
  PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS,
} from './constants'
import { generateCacheKey } from './helpers'

const DEFAULT_API_PARAMS = {
  category: null,
  includeMetrics: false,
  nameQuery: null,
  page: 0,
  pageSize: 25,
  onlyMine: false,
  sortDirection: 'asc',
  sortProperty: 'priority',
  filterEnabled: false,
}

const PlaybooksTableWrapper = ({
  playbookType,
  orgUsers,
  showTransientNotification,
  history,
}) => {
  const [apiParams, setApiParams] = useState(DEFAULT_API_PARAMS)
  const [showPrioritizedPlaybooks, setShowPrioritizedPlaybooks] =
    useState(false)

  const orgUsersById = useMemo(
    () => R.indexBy(R.prop('id'), orgUsers),
    [orgUsers]
  )

  const onChange = useCallback((params, callback = () => {}) => {
    setApiParams((oldParams) => ({ ...oldParams, ...params }))
    if (callback) callback()
  }, [])

  const cacheKey = apiParams.category ? generateCacheKey(apiParams) : null

  const playbooksSWR = useSWR(
    cacheKey,
    () =>
      getPlaybooks(apiParams).then(({ data }) => {
        return {
          meta: data.meta,
          playbooks: data.playbooks,
        }
      }),
    {
      dedupingInterval: 300000,
    }
  )

  useEffect(() => {
    const isValidCategory = PLAYBOOK_CATEGORIES[playbookType] || false

    if (isValidCategory) {
      const defaults = PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS[playbookType][0]

      onChange({
        category: defaults.value,
        page: 0,
        sortDirection: defaults.sortProperties.dir,
        sortProperty: defaults.sortProperties.property,
      })
    }
  }, [playbookType, onChange])

  const togglePrioritiesModal = () => {
    setShowPrioritizedPlaybooks(!showPrioritizedPlaybooks)
  }

  const openPriorityPageOrModal = (type) =>
    userHasPlaybookPriorityAccess()
      ? history.push('/priority/' + type)
      : togglePrioritiesModal()

  const initializeShowPriorityBanner = () =>
    playbookType === 'CHAT' &&
    // TODO: remove org-level priority feature check here once playbook priority has become global
    orgHasWorkspaceAccess() &&
    orgHasPlaybookPriorityFeature() &&
    !userHasPlaybookPriorityAccess()

  const [showPriorityAccessWarning, setShowPriorityAccessWarning] = useState(
    initializeShowPriorityBanner
  )

  return (
    <Flex
      direction="column"
      css={{
        margin: '$2',
        boxShadow: '$1',
        width: 'calc(100% - 32px)',
      }}
    >
      {showPriorityAccessWarning && (
        <PlaybookPriorityAccessWarning
          onClose={() => setShowPriorityAccessWarning(false)}
        />
      )}
      <TableHeader
        onChange={onChange}
        playbookType={playbookType}
        openPriorityPageOrModal={openPriorityPageOrModal}
      />
      <Table
        playbookType={playbookType}
        playbooksSWR={playbooksSWR ?? {}}
        apiParams={apiParams}
        onChange={onChange}
        orgUsers={orgUsersById}
        showTransientNotification={showTransientNotification}
        isPrioritizedPlaybooksOpen={showPrioritizedPlaybooks}
        history={history}
      />
      {showPrioritizedPlaybooks && (
        <PrioritizeModal
          onChange={playbooksSWR.mutate}
          orgUsers={orgUsers}
          showTransientNotification={showTransientNotification}
          togglePrioritiesModal={togglePrioritiesModal}
          playbookType={playbookType}
        />
      )}
    </Flex>
  )
}

export default PlaybooksTableWrapper
